import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { Subscription } from 'rxjs';
import { LoggingService } from '../../utils/logging.service';
import { HttpFormErrorService } from '../http-form-error.service';

/**
 * Diese Komponente kann optional überall benutzt werden, wo eine FormGroup benutzt wird
 * um die Fehler der FormGroup anzuzeigen. Diese Komponente muss nicht zwingend innerhalb
 * von <common-form></common-form> stehen. Sie zeigt einerseits direkt die "message"
 * aus einer "normalen" Exception an, ist aber vor allem gedacht um Fehlercodes mithilfe
 * von i18n aufzulösen.
 *
 * Das i18n-Feld ist daher nicht verpflichtend, wenn backendseitig gar keine Fehlercodes
 * für die gesamte FormGroup gedacht sind.
 *
 * Diese Komponente ist unabhängig von Feldfehlern und nur für Fehler, die an der gesamten
 * FormGroup hängen!
 *
 * Diese Komponente wird <b>am besten zusammen mit</b> {@link HttpFormErrorService}
 * benutzt.
 */
@Component({
    selector: 'common-form-errors',
    templateUrl: 'form-errors.component.html'
})
export class FormErrorsComponent implements OnInit, OnDestroy {

    /**
     * Die FormGroup, von der Fehler angezeigt werden. Es werden nur
     * Fehler direkt an der FormGroup angezeigt, d.h. keine Feldfehler.
     */
    @Input()
    form: UntypedFormGroup;

    /**
     * Klassen werden an ein wrappendes div weiter gegeben, damit sowas wie
     * "mt-1" etc. funktioniert.
     */
    @Input()
    clazz: string;

    /**
     * Optionales i18n-Lookup-Objekt, wenn an der FormGroup
     * Fehlercodes gesetzt werden. Das kommt auf den Anwendungsfall an.
     */
    @Input()
    i18n: {[key: string]: string};

    globalExceptionError: string;

    globalFieldErrorMsgs: string[] = [];

    private subscription: Subscription;

    constructor(private log: LoggingService) {
    }

    ngOnInit(): void {
        if (!this.form) {
            this.log.error('FormErrorsComponent: form ist null!');
            return;
        }

        if (!this.i18n) {
            // Das kann okay sein, wenn wir an einem Formular lediglich auf allgemeine
            // Exception Messages reagieren wollen; daher ist das kein Error sondern ur
            // eine Warning. Damit es eindeutiger ist, sollten aufrufende Stellen
            // [i18n]="{}" übergeben.
            this.log.warn('FormErrorsComponent: i18n ist null!');
        }

        this.subscription = this.form.statusChanges.subscribe(() => this.buildErrorMessages());
    }

    ngOnDestroy(): void {
        if (this.subscription) this.subscription.unsubscribe();
    }

    private buildErrorMessages(): void {
        this.globalExceptionError = null;
        this.globalFieldErrorMsgs = [];
        if (!this.form.errors) return;
        const errorKeys = Object.keys(this.form.errors);
        errorKeys.forEach(key => {
            if (key === HttpFormErrorService.REGULAR_EXCEPTION_ERROR) {
                // special case where we do no i18n because the actual message
                // is already set inside the errors object
                // the message comes from the ".message" field of an exception
                this.globalFieldErrorMsgs.push(this.form.getError(key));
            } else if (this.i18n && this.i18n[key]) {
                this.globalFieldErrorMsgs.push(this.i18n[key]);
            } else {
                this.log.error(`FormErrorsComponent: Es gibt kein i18n für den Fehlercode '${key}'`);
                this.globalFieldErrorMsgs.push(key);
            }
        });
    }

}
